import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TYPE } from '@/theme/typography';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Laptop as LaptopIcon,
  PhoneAndroid as PhoneIcon,
  Tablet as TabletIcon,
  DevicesOther as DevicesOtherIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { auditApi, AuditLogParams, AuditLogEntry, LoginHistoryEntry } from '@/api/endpoints/audit';
import StatusDot from '@/components/common/StatusDot';
import ExportMenu, { ExportColumn } from '@/components/common/ExportMenu';
import { useNotificationStore } from '@/stores/notificationStore';
import PageHelpIcon from '@/components/PageHelpIcon';

function DeviceIcon({ deviceType }: { deviceType: string | null }) {
  const type = (deviceType || '').toLowerCase();
  const sx = { fontSize: 18, color: 'text.secondary' };
  if (type === 'mobile') return <PhoneIcon sx={sx} />;
  if (type === 'tablet') return <TabletIcon sx={sx} />;
  if (type === 'desktop') return <LaptopIcon sx={sx} />;
  return <DevicesOtherIcon sx={sx} />;
}

// ── Action color mapping ──

const ACTION_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'primary' | 'default'> = {
  create: 'success',
  CREATE: 'success',
  update: 'info',
  UPDATE: 'info',
  delete: 'error',
  DELETE: 'error',
  void: 'error',
  VOID: 'error',
  login: 'primary',
  LOGIN: 'primary',
  logout: 'default',
  LOGOUT: 'default',
  approve: 'success',
  APPROVE: 'success',
  reject: 'warning',
  REJECT: 'warning',
  forfeit: 'warning',
  STATUS_CHANGE: 'info',
  switch_company: 'default',
  reset_password: 'warning',
  enable_2fa: 'success',
  disable_2fa: 'error',
  lock_period: 'warning',
  unlock_period: 'info',
  deactivate: 'error',
  import_statement: 'info',
  grant_advisor_access: 'success',
  revoke_advisor_access: 'error',
  // Auto-audit middleware actions
  confirm: 'success',
  cancel: 'warning',
  calculate: 'info',
  reconcile: 'info',
  sync: 'info',
  import: 'info',
  export: 'info',
  preview: 'default',
  commit: 'success',
  review: 'info',
  send: 'primary',
  broadcast: 'primary',
  reset: 'warning',
  seed: 'info',
  generate: 'info',
  refresh: 'default',
  run_now: 'primary',
  extend: 'info',
  grant: 'success',
  revoke: 'error',
  adjust: 'warning',
  transition: 'info',
  upload: 'info',
  bulk_delete: 'error',
  run_full: 'primary',
  apply_template: 'info',
  initiate: 'info',
  lock: 'warning',
  unlock: 'info',
  bulk: 'info',
};

function ActionChip({ action }: { action: string }) {
  const color = ACTION_COLORS[action] || 'default';
  const label = action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return (
    <Chip
      label={label}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontSize: TYPE.xs, height: 22, fontWeight: TYPE.semibold }}
    />
  );
}

// ── Filter options ──

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'void', label: 'Void' },
  { value: 'forfeit', label: 'Forfeit' },
  { value: 'approve', label: 'Approve' },
  { value: 'reject', label: 'Reject' },
  { value: 'confirm', label: 'Confirm' },
  { value: 'cancel', label: 'Cancel' },
  { value: 'calculate', label: 'Calculate' },
  { value: 'reconcile', label: 'Reconcile' },
  { value: 'sync', label: 'Sync' },
  { value: 'import', label: 'Import' },
  { value: 'export', label: 'Export' },
  { value: 'upload', label: 'Upload' },
  { value: 'send', label: 'Send' },
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'generate', label: 'Generate' },
  { value: 'seed', label: 'Seed' },
  { value: 'preview', label: 'Preview' },
  { value: 'commit', label: 'Commit' },
  { value: 'review', label: 'Review' },
  { value: 'adjust', label: 'Adjust' },
  { value: 'transition', label: 'Transition' },
  { value: 'grant', label: 'Grant Access' },
  { value: 'revoke', label: 'Revoke Access' },
  { value: 'extend', label: 'Extend' },
  { value: 'lock', label: 'Lock' },
  { value: 'unlock', label: 'Unlock' },
  { value: 'bulk_delete', label: 'Bulk Delete' },
  { value: 'run_full', label: 'Run Full' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'switch_company', label: 'Switch Company' },
  { value: 'reset_password', label: 'Password Reset' },
  { value: 'enable_2fa', label: 'Enable 2FA' },
  { value: 'disable_2fa', label: 'Disable 2FA' },
  { value: 'STATUS_CHANGE', label: 'Status Change' },
  { value: 'lock_period', label: 'Lock Period' },
  { value: 'unlock_period', label: 'Unlock Period' },
  { value: 'deactivate', label: 'Deactivate' },
  { value: 'import_statement', label: 'Import Statement' },
  { value: 'grant_advisor_access', label: 'Grant Advisor' },
  { value: 'revoke_advisor_access', label: 'Revoke Advisor' },
];

const TABLE_OPTIONS = [
  { value: '', label: 'All Tables' },
  // Accounting
  { value: 'revenue_transactions', label: 'Revenue' },
  { value: 'expense_transactions', label: 'Expenses' },
  { value: 'journal_entries', label: 'Journal Entries' },
  { value: 'journal_lines', label: 'Journal Lines' },
  { value: 'accounts', label: 'Chart of Accounts' },
  { value: 'bank_statements', label: 'Bank Statements' },
  { value: 'tracking_categories', label: 'Tracking Categories' },
  { value: 'financial_periods', label: 'Financial Periods' },
  { value: 'invoices', label: 'Invoices' },
  // HR & Payroll
  { value: 'employees', label: 'Employees' },
  { value: 'payroll_runs', label: 'Payroll Runs' },
  { value: 'payroll_lines', label: 'Payroll Lines' },
  { value: 'leave_requests', label: 'Leave Requests' },
  { value: 'leave_balances', label: 'Leave Balances' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'recruitment', label: 'Recruitment' },
  { value: 'documents', label: 'Documents' },
  { value: 'performance', label: 'Performance' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'garnishments', label: 'Garnishments' },
  { value: 'org_chart', label: 'Org Chart' },
  // Property & Tenants
  { value: 'tenants', label: 'Tenants' },
  { value: 'leases', label: 'Leases' },
  { value: 'rent_payments', label: 'Rent Payments' },
  { value: 'rent_receivables', label: 'Rent Receivables' },
  { value: 'locations', label: 'Locations' },
  { value: 'buildings', label: 'Buildings' },
  { value: 'units', label: 'Units' },
  // Group & Company
  { value: 'companies', label: 'Companies' },
  { value: 'intercompany_transactions', label: 'Intercompany' },
  { value: 'budgets', label: 'Budgets' },
  // Settings & Admin
  { value: 'users', label: 'Users' },
  { value: 'roles', label: 'Roles' },
  { value: 'reference_data', label: 'Reference Data' },
  { value: 'custom_field_definitions', label: 'Custom Fields' },
  { value: 'announcements', label: 'Announcements' },
  { value: 'module_configs', label: 'Modules' },
  { value: 'notification_templates', label: 'Notification Templates' },
  { value: 'data_imports', label: 'Data Imports' },
  { value: 'holidays', label: 'Holidays' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'automation', label: 'Automation' },
  { value: 'workflows', label: 'Workflows' },
  { value: 'settings', label: 'Settings' },
  // Messaging & Comms
  { value: 'messaging', label: 'Messaging' },
  { value: 'card_templates', label: 'Card Templates' },
  { value: 'greetings', label: 'Greetings' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'notifications', label: 'Notifications' },
  // Other
  { value: 'advisor_access', label: 'Advisor Access' },
  { value: 'ai_chat', label: 'AI Chat' },
  { value: 'billing', label: 'Billing' },
  { value: 'portal', label: 'Portal' },
];

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}

// ── Change Detail Panel ──

function ChangeDetail({ entry }: { entry: AuditLogEntry }) {
  const hasOld = entry.old_values && Object.keys(entry.old_values).length > 0;
  const hasNew = entry.new_values && Object.keys(entry.new_values).length > 0;

  if (!hasOld && !hasNew) {
    return (
      <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
        No change details recorded for this entry.
      </Typography>
    );
  }

  // For updates: show side-by-side diff of changed fields
  if (hasOld && hasNew) {
    const allKeys = new Set([
      ...Object.keys(entry.old_values || {}),
      ...Object.keys(entry.new_values || {}),
    ]);
    const changedKeys = Array.from(allKeys).filter(
      (k) => JSON.stringify((entry.old_values as any)?.[k]) !== JSON.stringify((entry.new_values as any)?.[k])
    );

    if (changedKeys.length === 0) {
      return (
        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
          No fields changed.
        </Typography>
      );
    }

    return (
      <Table size="small" sx={{ '& td': { py: 0.5 } }}>
        <TableBody>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: TYPE.bold, width: 160 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: TYPE.bold, color: 'error.main' }}>Before</TableCell>
            <TableCell sx={{ fontWeight: TYPE.bold, color: 'success.main' }}>After</TableCell>
          </TableRow>
          {changedKeys.map((key) => (
            <TableRow key={key}>
              <TableCell sx={{ fontWeight: TYPE.medium, color: 'text.secondary' }}>
                {key.replace(/_/g, ' ')}
              </TableCell>
              <TableCell sx={{ fontFamily: TYPE.mono, color: 'error.main', bgcolor: (t) => `${t.palette.error.main}08` }}>
                {formatValue((entry.old_values as any)?.[key])}
              </TableCell>
              <TableCell sx={{ fontFamily: TYPE.mono, color: 'success.main', bgcolor: (t) => `${t.palette.success.main}08` }}>
                {formatValue((entry.new_values as any)?.[key])}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // For creates: show new values only
  if (hasNew) {
    return (
      <Table size="small" sx={{ '& td': { py: 0.5 } }}>
        <TableBody>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: TYPE.bold, width: 160 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: TYPE.bold }}>Value</TableCell>
          </TableRow>
          {Object.entries(entry.new_values || {}).map(([key, val]) => (
            <TableRow key={key}>
              <TableCell sx={{ fontWeight: TYPE.medium, color: 'text.secondary' }}>
                {key.replace(/_/g, ' ')}
              </TableCell>
              <TableCell sx={{ fontFamily: TYPE.mono }}>{formatValue(val)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // For deletes/voids: show old values
  return (
    <Table size="small" sx={{ '& td': { py: 0.5 } }}>
      <TableBody>
        <TableRow sx={{ bgcolor: 'action.hover' }}>
          <TableCell sx={{ fontWeight: TYPE.bold, width: 160 }}>Field</TableCell>
          <TableCell sx={{ fontWeight: TYPE.bold }}>Previous Value</TableCell>
        </TableRow>
        {Object.entries(entry.old_values || {}).map(([key, val]) => (
          <TableRow key={key}>
            <TableCell sx={{ fontWeight: TYPE.medium, color: 'text.secondary' }}>
              {key.replace(/_/g, ' ')}
            </TableCell>
            <TableCell sx={{ fontFamily: TYPE.mono }}>{formatValue(val)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

// ── Login History Tab ──

function LoginHistoryTab() {
  const [page, setPage] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const { data, isLoading } = useQuery({
    queryKey: ['audit/login-history', page.page, page.pageSize],
    queryFn: () =>
      auditApi.loginHistory({ page: page.page + 1, per_page: page.pageSize }).then((r) => r.data),
  });

  const columns: GridColDef<LoginHistoryEntry>[] = [
    {
      field: 'user_name',
      headerName: 'User',
      flex: 1,
      minWidth: 110,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
          <Typography variant="body2" fontWeight={TYPE.medium}>
            {p.value || '—'}
          </Typography>
          {p.row.user_email && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
              {p.row.user_email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'login_at',
      headerName: 'Login',
      flex: 0.9,
      minWidth: 110,
      renderCell: (p) => (
        <Typography variant="body2">
          {new Date(p.value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
        </Typography>
      ),
    },
    {
      field: 'logout_at',
      headerName: 'Logout',
      flex: 0.9,
      minWidth: 110,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ color: p.value ? 'text.primary' : 'text.disabled' }}>
          {p.value
            ? new Date(p.value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
            : '—'}
        </Typography>
      ),
    },
    {
      field: 'browser',
      headerName: 'Browser',
      flex: 1,
      minWidth: 110,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
          <Typography variant="body2" fontWeight={TYPE.medium}>
            {p.row.browser || '—'}
          </Typography>
          {p.row.os && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
              on {p.row.os}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 100,
      renderCell: (p) => (
        <Typography variant="body2" color="text.secondary">
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'device_type',
      headerName: 'Device',
      flex: 0.6,
      minWidth: 80,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <DeviceIcon deviceType={p.value} />
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {p.value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'success',
      headerName: 'Result',
      flex: 0.6,
      minWidth: 90,
      renderCell: (p) => (
        <StatusDot active={p.value} activeLabel="Authenticated" inactiveLabel="Denied" />
      ),
    },
    {
      field: 'ip_address',
      headerName: 'IP Address',
      flex: 0.85,
      minWidth: 110,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: TYPE.mono, color: 'text.secondary' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Card>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <DataGrid
            autoHeight
            density="compact"
            rows={data?.items || []}
            columns={columns}
            rowCount={data?.total || 0}
            loading={isLoading}
            paginationMode="server"
            paginationModel={page}
            onPaginationModelChange={setPage}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            localeText={{ noRowsLabel: 'No login history found.' }}
          />
      </CardContent>
    </Card>
  );
}

// ── Main Audit Log Page ──

export default function AuditLogPage() {
  const { showError } = useNotificationStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'logins' ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === 1) next.set('tab', 'logins');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  // Pagination
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const buildParams = useCallback((): AuditLogParams => {
    const params: AuditLogParams = {
      page: paginationModel.page + 1,
      per_page: paginationModel.pageSize,
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (userFilter.trim()) params.user_id = userFilter.trim();
    if (tableFilter) params.table_name = tableFilter;
    if (actionFilter) params.action = actionFilter;
    return params;
  }, [paginationModel, startDate, endDate, userFilter, tableFilter, actionFilter]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'audit/logs',
      paginationModel.page,
      paginationModel.pageSize,
      startDate,
      endDate,
      userFilter,
      tableFilter,
      actionFilter,
    ],
    queryFn: () => auditApi.list(buildParams()).then((res) => res.data),
  });

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setUserFilter('');
    setTableFilter('');
    setActionFilter('');
    setPaginationModel({ page: 0, pageSize: 25 });
  };

  const hasActiveFilters = startDate || endDate || userFilter || tableFilter || actionFilter;

  const handleExportCsv = async () => {
    try {
      const params = buildParams();
      delete params.page;
      delete params.per_page;
      const response = await auditApi.exportCsv(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      showError('Failed to export audit logs');
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const columns: GridColDef<AuditLogEntry>[] = [
    {
      field: 'expand',
      headerName: '',
      width: 40,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasDetail =
          (params.row.old_values && Object.keys(params.row.old_values).length > 0) ||
          (params.row.new_values && Object.keys(params.row.new_values).length > 0);
        if (!hasDetail) return null;
        const isOpen = expandedRow === params.row.id;
        return (
          <IconButton size="small" onClick={() => toggleRow(params.row.id)}>
            {isOpen ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        );
      },
    },
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      flex: 0.9,
      minWidth: 115,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatTimestamp(params.value) : '—'}
        </Typography>
      ),
    },
    {
      field: 'user_name',
      headerName: 'User',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
          <Typography variant="body2" fontWeight={TYPE.medium} noWrap>
            {params.row.user_name || '—'}
          </Typography>
          {params.row.user_email && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
              {params.row.user_email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.8,
      minWidth: 90,
      renderCell: (params) => <ActionChip action={params.value as string} />,
    },
    {
      field: 'table_name',
      headerName: 'Table',
      flex: 1,
      minWidth: 90,
      renderCell: (params) => {
        const value = params.value || '—';
        return (
          <Typography variant="body2" noWrap>
            {value.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
          </Typography>
        );
      },
    },
    {
      field: 'record_id',
      headerName: 'Record',
      flex: 0.7,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: TYPE.mono, fontSize: TYPE.xs }} color="text.secondary" noWrap>
          {params.value ? String(params.value).slice(0, 8) + '...' : '—'}
        </Typography>
      ),
    },
    {
      field: 'ip_address',
      headerName: 'IP Address',
      flex: 0.75,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: TYPE.mono }} color="text.secondary">
          {params.value || '—'}
        </Typography>
      ),
    },
  ];

  const items = data?.items || [];

  const exportColumns: ExportColumn[] = [
    { field: 'timestamp', headerName: 'Timestamp', valueGetter: (row) => row.timestamp ? formatTimestamp(String(row.timestamp)) : '—' },
    { field: 'user_name', headerName: 'User' },
    { field: 'action', headerName: 'Action' },
    { field: 'table_name', headerName: 'Table' },
    { field: 'record_id', headerName: 'Record ID' },
  ];

  return (
    <Box>
      {activeTab === 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <PageHelpIcon pageKey="audit" />
          <Stack direction="row" spacing={1}>
            <ExportMenu
              columns={exportColumns}
              rows={items}
              filename={`audit-log-${new Date().toISOString().split('T')[0]}`}
              title="Audit Log"
            />
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={!data || data.total === 0}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.md },
        }}
      >
        <Tab label={`Audit Logs${data?.total ? ` (${data.total.toLocaleString()})` : ''}`} />
        <Tab label="Login History" />
      </Tabs>

      {activeTab === 1 && <LoginHistoryTab />}

      {activeTab === 0 && (
        <>
          {/* Filters — single row */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <FilterIcon color="action" sx={{ fontSize: 18 }} />
            <TextField
              label="Start"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: TYPE.sm }, '& .MuiInputLabel-root': { fontSize: TYPE.sm } }}
            />
            <TextField
              label="End"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: TYPE.sm }, '& .MuiInputLabel-root': { fontSize: TYPE.sm } }}
            />
            <TextField
              label="User"
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
              size="small"
              placeholder="ID or email"
              sx={{ flex: 1.2, minWidth: 0, '& .MuiInputBase-input': { fontSize: TYPE.sm }, '& .MuiInputLabel-root': { fontSize: TYPE.sm } }}
            />
            <TextField
              label="Table"
              select
              value={tableFilter}
              onChange={(e) => { setTableFilter(e.target.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
              size="small"
              sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: TYPE.sm }, '& .MuiInputLabel-root': { fontSize: TYPE.sm }, '& .MuiSelect-select': { fontSize: TYPE.sm } }}
            >
              {TABLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: TYPE.sm }}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Action"
              select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
              size="small"
              sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: TYPE.sm }, '& .MuiInputLabel-root': { fontSize: TYPE.sm }, '& .MuiSelect-select': { fontSize: TYPE.sm } }}
            >
              {ACTION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: TYPE.sm }}>{opt.label}</MenuItem>
              ))}
            </TextField>
            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{ fontSize: TYPE.sm, whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Data Grid + Expandable Detail */}
          <Card>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <DataGrid
                autoHeight
                density="compact"
                rows={items}
                columns={columns}
                rowCount={data?.total || 0}
                loading={isLoading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                  setPaginationModel(model);
                  setExpandedRow(null);
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
                }}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  expandedRow === params.row.id ? 'audit-row-expanded' : ''
                }
                onRowClick={(params) => {
                  const hasDetail =
                    (params.row.old_values && Object.keys(params.row.old_values).length > 0) ||
                    (params.row.new_values && Object.keys(params.row.new_values).length > 0);
                  if (hasDetail) toggleRow(params.row.id);
                }}
                sx={{
                  cursor: 'pointer',
                  '& .audit-row-expanded': { bgcolor: 'action.selected' },
                }}
                slotProps={{
                  loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
                }}
                localeText={{
                  noRowsLabel: 'No audit log entries found.',
                  MuiTablePagination: { labelRowsPerPage: 'Rows per page:' },
                }}
              />

              {/* Expanded Detail Panel */}
              {expandedRow && (
                <Collapse in={!!expandedRow}>
                  <Paper
                    sx={{
                      mx: 2, mb: 2, p: 2, border: '1px solid',
                      borderColor: 'primary.light', borderRadius: 1,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={TYPE.bold} sx={{ mb: 1 }}>
                      Change Details
                    </Typography>
                    {(() => {
                      const entry = items.find((i) => i.id === expandedRow);
                      if (!entry) return null;
                      return <ChangeDetail entry={entry} />;
                    })()}
                  </Paper>
                </Collapse>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
